/**
 * Permission model (cahier des charges §4).
 *
 * - Global roles: owner, admin, member.
 * - Members only act through explicit grants scoped to a project and,
 *   for environment-bound actions, to one environment. A grant on the
 *   development environment never implies anything on production.
 * - The server is the only enforcement point; the UI uses the same
 *   catalogue purely to hide actions that would be refused.
 */
export const GLOBAL_ROLES = ['owner', 'admin', 'member'];
/** Actions that only make sense on the whole project. */
export const PROJECT_ACTIONS = [
    'project.read',
    'project.update',
    'project.archive',
    'project.access.manage',
    'changelog.manage',
    'repository.manage',
    'environment.manage',
    'security.manage',
    'incident.manage',
];
/** Actions always bound to a specific environment. */
export const ENVIRONMENT_ACTIONS = [
    'environment.read',
    'secret.reveal',
    'secret.manage',
    'backup.read',
    'backup.run',
    'backup.delete',
    'backup.policy.manage',
    'restore.run',
    'deploy.run',
    'domain.manage',
    'monitor.manage',
    'alert.ack',
];
/** Global actions. `owner` only, unless listed in ADMIN_GLOBAL_ACTIONS. */
export const GLOBAL_ACTIONS = [
    'settings.manage',
    'users.manage',
    'connectors.manage',
    'security.policies.manage',
    'audit.read',
    'audit.export',
    'project.create',
    'console.backup',
];
/** What an administrator may do globally (everything except owner-reserved rights). */
export const ADMIN_GLOBAL_ACTIONS = ['audit.read', 'project.create', 'console.backup'];
export function isEnvironmentAction(action) {
    return ENVIRONMENT_ACTIONS.includes(action);
}
export function isProjectAction(action) {
    return PROJECT_ACTIONS.includes(action);
}
export function isGlobalAction(action) {
    return GLOBAL_ACTIONS.includes(action);
}
/** Grant presets proposed in the UI. They are expanded into explicit action lists. */
export const GRANT_PRESETS = {
    viewer: {
        project: ['project.read'],
        environment: ['environment.read', 'backup.read'],
    },
    developer: {
        project: ['project.read', 'changelog.manage', 'repository.manage', 'security.manage', 'incident.manage'],
        environment: ['environment.read', 'backup.read', 'alert.ack', 'monitor.manage'],
    },
    operator: {
        project: ['project.read', 'changelog.manage', 'incident.manage'],
        environment: [
            'environment.read',
            'backup.read',
            'backup.run',
            'backup.policy.manage',
            'restore.run',
            'deploy.run',
            'domain.manage',
            'monitor.manage',
            'alert.ack',
        ],
    },
};
/**
 * Pure decision function. Returns true only when the principal is
 * explicitly allowed to perform `action` on `scope`.
 */
export function can(principal, action, scope = {}) {
    if (principal.role === 'owner')
        return true;
    if (isGlobalAction(action)) {
        return principal.role === 'admin' && ADMIN_GLOBAL_ACTIONS.includes(action);
    }
    if (principal.role === 'admin')
        return true;
    // Members: explicit grants only.
    const { projectId, environmentId } = scope;
    if (!projectId)
        return false;
    if (isEnvironmentAction(action)) {
        if (!environmentId)
            return false;
        return principal.grants.some((g) => g.projectId === projectId && g.environmentId === environmentId && g.actions.includes(action));
    }
    return principal.grants.some((g) => g.projectId === projectId && g.environmentId === null && g.actions.includes(action));
}
/** A member can see a project as soon as they hold `project.read` on it. */
export function canSeeProject(principal, projectId) {
    return can(principal, 'project.read', { projectId });
}
export function visibleProjectIds(principal) {
    if (principal.role !== 'member')
        return 'all';
    return [
        ...new Set(principal.grants.filter((g) => g.environmentId === null && g.actions.includes('project.read')).map((g) => g.projectId)),
    ];
}
//# sourceMappingURL=permissions.js.map